/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { CallStatus } from '../slice/constants';
import useGraphQLFunctions from './useGraphQLFunctions';
// import { v4 as uuidv4 } from 'uuid';
import { useSubscription } from '@apollo/client/react';
import { ON_ANSWER, ON_CALL_READY, ON_ICE_CANDIDATE, ON_CALL_STATUS_CHANGE } from './gql';
import useGetTurncred from './useGetTurncred';
import { useDispatch, useSelector } from 'react-redux';
import { usePortalSlice } from '../../../layouts/MainLayout/slice';
import { SESSION_TYPES } from '../../chat/slice/constants';
import { selectCurrentProduct, selectSessionUserId } from '../../../layouts/MainLayout/slice/selectors';
import { request } from '../../../helpers/request';
import { API } from '../slice/constants';
import { useStopwatch } from 'react-timer-hook';

interface HTMLAudioElementWithInline extends HTMLAudioElement {
  playsInline?: boolean;
}

export default function useCallHandler() {
  const [callStatus, setCallStatus] = useState<CallStatus>(CallStatus.IDLE);
  const [backendStatus, setBackendStatus] = useState(CallStatus.LISTENING);
  const audioStream = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElementWithInline | null>(null);
  const remoteAudioStreamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const { addIceCandidateMutation, createOfferMutation } = useGraphQLFunctions();
  const currentSessionId = useSelector(selectSessionUserId);
  const callerIdRef = useRef<string>(currentSessionId || '');
  const callerId = callerIdRef.current;
  const pendingRemoteCandsRef = useRef<RTCIceCandidateInit[]>([]);
  const getTurnCred = useGetTurncred();
  const dispatch = useDispatch();
  const { actions } = usePortalSlice();
  const productId = useSelector(selectCurrentProduct);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const { seconds, minutes, isRunning, start, pause, reset } = useStopwatch({ autoStart: false, interval: 20 });

  useSubscription(ON_ANSWER, {
    variables: { sessionId: sessionId },
    onData: async ({ data }: { data: any }) => {
      const answer = data.data?.onAnswer?.onAnswer;

      const pc = pcRef.current;
      if (!answer || !pc) return;

      await pc.setRemoteDescription(answer);
      for (const c of pendingRemoteCandsRef.current) {
        try {
          await pc.addIceCandidate(c);
        } catch (e) {
          console.warn(e);
        }
      }
      pendingRemoteCandsRef.current = [];
    },
  });

  useSubscription(ON_ICE_CANDIDATE, {
    variables: { sessionId: sessionId },
    onData: async ({ data }: { data: any }) => {
      const { candidate } = data.data?.onIceCandidate;
      const pc = pcRef.current;
      if (!candidate || !pc) return;
      if (candidate.candidate === '') {
        try {
          await pc.addIceCandidate(undefined);
        } catch {}
        return;
      }
      if (!pc.remoteDescription) {
        pendingRemoteCandsRef.current.push(candidate);
      } else {
        try {
          await pc.addIceCandidate(candidate);
        } catch (e) {
          console.warn(e);
        }
      }
    },
  });

  useSubscription(ON_CALL_READY, {
    variables: { sessionId: sessionId },
    onData: ({ data }: { data: any }) => {
      const callReady = data.data?.onCallReady;
      if (!callReady) return;
      setCallStatus(CallStatus.CONNECTED);
      start();
    },
  });

  useSubscription(ON_CALL_STATUS_CHANGE, {
    variables: { sessionId: sessionId },
    onData: ({ data }: { data: any }) => {
      const status = data.data?.onCallStatusChange;
      if (!status) return;
      setBackendStatus(status);
      if (status === CallStatus.TIMEOUT) {
        handleEndCall({ silent: true });
        setCallStatus(CallStatus.ERROR);
        setError('Call ended automatically. Please start a new call.');
      }
    },
  });

  const handleStartCall = async () => {
    setCallStatus(CallStatus.CONNECTING);
    reset(undefined, false);
    try {
      const turncred = await getTurnCred();

      if (!turncred) {
        setCallStatus(CallStatus.ERROR);
        return;
      }
      // Set audio sample rate to 16000

      let _sessionId: string | null = null;

      // Should create the session at database first
      try {
        const response = await request(API.CREATE_SESSION, { userId: callerId, title: new Date().toISOString(), sessionType: 'call', productId: productId }, true);
        if (response.success) {
          _sessionId = response.data._id;
          setSessionId(_sessionId);
          await new Promise((resolve) => setTimeout(resolve, 1000));
        } else {
          console.error('Error creating session', response.message);
          setCallStatus(CallStatus.ERROR);
          return;
        }
      } catch (error) {
        console.error('Error creating session', error);
        setCallStatus(CallStatus.ERROR);
        return;
      }

      const audioConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      };
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
        video: false,
      });
      audioStream.current = stream;

      const iceServers = [{ urls: 'stun:stun.l.google.com:19302' }, turncred];

      const pc = new RTCPeerConnection({
        iceServers: iceServers as RTCIceServer[],
      });
      pcRef.current = pc;

      pc.onicecandidate = (event) => {
        if (!event.candidate || !_sessionId) return;
        const json = event.candidate.toJSON?.() ?? event.candidate;
        addIceCandidateMutation({
          variables: { callId: callerId, candidate: json, sessionId: _sessionId },
        });
      };

      pc.oniceconnectionstatechange = () => {
        const s = pc.iceConnectionState;
        // if (s === 'connected' || s === 'completed') setCallStatus(CallStatus.CONNECTED);
        if (s === 'failed' || s === 'disconnected' || s === 'closed') {
          setCallStatus(CallStatus.ERROR);
        }
      };

      // Server->client audio
      pc.ontrack = (e) => {
        console.log('ontrack', e.track.kind, e.streams);
        let stream = e.streams?.[0];

        if (!stream) stream = new MediaStream([e.track]);
        remoteAudioStreamRef.current = stream;
        const el = remoteAudioRef.current!;
        el.srcObject = stream;
        el.muted = false;
        el.volume = 1;

        const log = (name: string) => () => (el.onloadedmetadata = log('loadedmetadata'));
        el.oncanplay = log('canplay');
        el.onplay = log('play');
        el.onpause = log('pause');
        el.onerror = () => console.warn('[remoteAudio] error', el.error);

        el.play().catch((err) => {
          console.warn('autoplay blocked; waiting for user gesture', err);
          const retryPlay = () => {
            el.play()
              .then(() => document.removeEventListener('click', retryPlay))
              .catch(() => {});
          };
          document.addEventListener('click', retryPlay);
        });
      };

      // Add ONLY the local mic; do NOT add a second audio transceiver
      stream.getAudioTracks().forEach((t) => {
        t.enabled = true; // ensure not muted
        pc.addTrack(t, stream);
      });

      // Create and send offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await createOfferMutation({
        variables: { callId: callerId, offer, productId, sessionId: _sessionId },
      });
    } catch (error) {
      console.log(error);
      setCallStatus(CallStatus.ERROR);
      reset(undefined, false);
    }
  };

  const handleEndCall = (options?: { silent?: boolean }) => {
    try {
      audioStream.current?.getTracks().forEach((t) => t.stop());
      audioStream.current = null;
    } catch {}

    const pc = pcRef.current;
    if (pc) {
      try {
        pc.getSenders().forEach((s) => s.track?.stop());
        pc.getReceivers().forEach((r) => r.track?.stop?.());
        pc.getTransceivers?.().forEach((tx) => tx.stop?.());
        pc.onicecandidate = null;
        pc.ontrack = null;
        pc.oniceconnectionstatechange = null;
        pc.onconnectionstatechange = null;
        pc.close();
      } catch {}
      pcRef.current = null;
    }

    try {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
        remoteAudioRef.current.pause();
      }
      remoteAudioStreamRef.current = null;
    } catch {}

    if (!options?.silent) {
      setCallStatus(CallStatus.DISCONNECTED);
      dispatch(actions.changeHistoryType(SESSION_TYPES.CALL));
    }
    pause();
    setSessionId(null);
  };

  const handleToggleMute = () => {
    if (!audioStream.current) return;
    const next = !isMuted;
    audioStream.current.getAudioTracks().forEach((t) => (t.enabled = !next));
    setIsMuted(next);
  };

  useEffect(() => {
    if (callStatus === CallStatus.ERROR) {
      if (audioStream.current) {
        audioStream.current.getTracks().forEach((track) => track.stop());
      }
      if (pcRef.current) {
        pcRef.current.close();
      }
    }
  }, [callStatus]);

  useEffect(() => {
    if (!audioStream.current) return;
    if (callStatus !== CallStatus.CONNECTED) return;
    if (backendStatus !== CallStatus.LISTENING && !isMuted) {
      audioStream.current.getAudioTracks().forEach((t) => (t.enabled = false));
    } else if (backendStatus === CallStatus.LISTENING && !isMuted) {
      audioStream.current.getAudioTracks().forEach((t) => (t.enabled = true));
    }
  }, [backendStatus, isMuted, callStatus]);

  useEffect(() => {
    if (!remoteAudioRef.current) {
      const el = new Audio() as HTMLAudioElementWithInline;
      el.autoplay = true;
      el.playsInline = true;
      el.crossOrigin = 'anonymous';
      el.preload = 'auto';
      remoteAudioRef.current = el;
      document.body.appendChild(el);
    }
    return () => {
      handleEndCall({ silent: true });
    };
  }, []);

  const spectrumColors = backendStatus === CallStatus.SPEAKING ? ['#F59E0B', '#EF4444', '#FB7185'] : ['#7C3AED', '#3B82F6', '#06B6D4'];
  const spectrumStream = backendStatus === CallStatus.SPEAKING ? remoteAudioStreamRef?.current ?? null : audioStream?.current ?? null;
  const shouldShowSpectrum = (backendStatus === CallStatus.LISTENING && !isMuted && !!audioStream?.current) || (backendStatus === CallStatus.SPEAKING && !!remoteAudioStreamRef?.current);

  return {
    callStatus,
    setCallStatus,
    handleStartCall,
    handleEndCall,
    handleToggleMute,
    isMuted,
    remoteAudioRef,
    backendStatus,
    localAudioStreamRef: audioStream,
    remoteAudioStreamRef,
    spectrumColors,
    spectrumStream,
    shouldShowSpectrum,
    minutes,
    seconds,
    isRunning,
    error,
  };
}
